import { useEffect, useState } from "react";
import { useShakespeare, type Model } from "@/hooks/useShakespeare";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ModelSelectProps {
  value: string;
  onChange: (modelId: string) => void;
}

export function ModelSelect({ value, onChange }: ModelSelectProps) {
  const { getAvailableModels, isAuthenticated } = useShakespeare();
  const [models, setModels] = useState<Model[]>([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getAvailableModels()
      .then((res) => {
        if (cancelled) return;
        const sorted = [...res.data].sort((a, b) => {
          const ca = parseFloat(a.pricing.prompt) + parseFloat(a.pricing.completion);
          const cb = parseFloat(b.pricing.prompt) + parseFloat(b.pricing.completion);
          return ca - cb;
        });
        setModels(sorted);
        if (!value && sorted.length) {
          // Prefer a capable default; fall back to cheapest.
          const preferred =
            sorted.find((m) => /claude|gpt-4|sonnet|opus|gemini/i.test(m.id)) ?? sorted[0];
          onChange(preferred.id);
        }
      })
      .catch(() => {
        /* surfaced elsewhere */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-auto gap-1 border-none bg-transparent text-xs text-muted-foreground shadow-none hover:text-foreground focus:ring-0">
        <SelectValue placeholder="Select model" />
      </SelectTrigger>
      <SelectContent>
        {models.map((m) => {
          const free = m.pricing.prompt === "0" && m.pricing.completion === "0";
          return (
            <SelectItem key={m.id} value={m.id} className="text-xs">
              {m.name}
              {free ? " · free" : ""}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
